import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import logoGo from "../../assets/logo-go.png";
import { MdNavigateNext } from "react-icons/md";

interface Price {
  amount: number;
  currency: string;
  negotiable: boolean;
}

interface Address {
  street: string;
  city: string;
  postalCode: string;
  country: string;
}

interface Building {
  address: Address;
  _id: string;
  name: string;
  features: string[];
}

interface ApartmentFeatures {
  water: boolean;
  electricity: boolean;
  gas: boolean;
  furnished: boolean;
  airConditioning: boolean;
  balcony: boolean;
  internet: boolean;
  parking: boolean;
  securitySystem: boolean;
  elevator: boolean;
  garden: boolean;
  terrace: boolean;
  fitted_kitchen: boolean;
  pool: boolean;
}

interface Availability {
  immediateMove: boolean;
}

interface Requirements {
  documents: string[];
}

interface ContactPreferences {
  phone: boolean;
  whatsapp: boolean;
  email: boolean;
}

interface Apartment {
  _id: string;
  buildingId: Building;
  type: string;
  surface: number;
  rooms: number;
  bathrooms: number;
  price: {
    amount: number;
    currency: string;
    paymentFrequency: string;
  };
  features: ApartmentFeatures;
  images: string[];
}

interface Publisher {
  _id: string;
  phone: string;
  firstName: string;
  lastName: string;
}

interface AnnonceData {
  _id: string;
  title: string;
  description: string;
  price: Price;
  availability: Availability;
  requirements: Requirements;
  contactPreferences: ContactPreferences;
  apartmentId: Apartment;
  publisher: Publisher;
  status: string;
  views: number;
  createdAt: string;
  updatedAt: string;
}

const LoadingSkeleton = () => (
  <div className="min-h-screen bg-gray-50 animate-pulse">
    {/* Logo Section */}
    <div className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="h-16 sm:h-20 w-48 bg-gray-200 rounded-lg" />
      </div>
    </div>

    {/* Hero Section */}
    <div className="relative h-[50vh] bg-gray-200" />

    {/* Content Skeleton */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pb-16">
      <div className="bg-white rounded-xl shadow-lg -mt-32 overflow-hidden">
        <div className="p-6 sm:p-8 lg:p-10 space-y-6">
          {/* Title */}
          <div className="h-8 bg-gray-200 rounded w-3/4" />
          
          {/* Price */}
          <div className="h-10 bg-gray-200 rounded w-1/4" />
          
          {/* Features */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 rounded" />
            ))}
          </div>
          
          {/* Description */}
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-4 bg-gray-200 rounded w-4/6" />
          </div>
        </div>
      </div>

      {/* Contact Card */}
      <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-6" />
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-gray-200 rounded-full" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="h-3 bg-gray-200 rounded w-24" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Annonce: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [annonce, setAnnonce] = useState<AnnonceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<number>(0);

  const fetchAnnonce = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching annonce with ID:", id);
      const response = await fetch(
        // `http://68.183.30.146:8000/api/v1/listings/${id}`
        `https://api-immo.vercel.app/api/v1/listings/${id}`
      );
      console.log("Response status:", response.status);
      if (!response.ok) {
        throw new Error(
          response.status === 404 
            ? "Cette annonce n'existe pas ou a été supprimée" 
            : "Un problème est survenu lors du chargement de l'annonce"
        );
      }
      const { data } = await response.json();
      console.log("Structure des données reçues:", JSON.stringify(data, null, 2));
      
      // Vérification détaillée de la structure
      if (!data) {
        throw new Error("Aucune donnée reçue");
      }
      if (!data.apartmentId) {
        console.error("Structure invalide - apartmentId manquant:", data);
        throw new Error("Structure invalide - apartmentId manquant");
      }
      if (!data.apartmentId.buildingId) {
        console.error("Structure invalide - buildingId manquant:", data.apartmentId);
        throw new Error("Structure invalide - buildingId manquant");
      }
      
      setAnnonce(data);
    } catch (err) {
      console.error("Error fetching annonce:", err);
      setError(
        err instanceof Error 
          ? err.message 
          : "Une erreur inattendue s'est produite"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchAnnonce();
    }
  }, [id]);

  if (loading) return <LoadingSkeleton />;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-auto text-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">Une erreur est survenue</h3>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={fetchAnnonce}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Réessayer
            </button>
            <a
              href="/"
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Retour à l'accueil
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  if (!annonce) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900">Annonce non trouvée</h2>
        <p className="mt-2 text-gray-600">Cette annonce n'existe pas ou a été supprimée</p>
        <div className="mt-6">
          <a
            href="/"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Retour à l'accueil
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Logo */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <img src={logoGo} alt="ImmGo" className="h-16 sm:h-20 w-auto" />
        </div>
      </div>

      {/* Hero Section */}
      <div
        className="relative h-[50vh] bg-cover bg-center"
        style={{
          backgroundImage: `url(${annonce?.apartmentId?.images?.[selectedImage] || '/default-property.jpg'})`
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40" />
      </div>

      {/* Image Gallery */}
      {annonce?.apartmentId?.images && annonce.apartmentId.images.length > 1 && (
        <div className="relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white p-4 rounded-xl shadow-lg -mt-20 mb-8">
              <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
                {annonce.apartmentId.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-none relative rounded-lg overflow-hidden ${
                      selectedImage === index 
                        ? 'ring-4 ring-red-600 ring-offset-2' 
                        : 'hover:opacity-75 transition-opacity'
                    }`}
                  >
                    <div className="relative w-32 h-24">
                      <img
                        src={image}
                        alt={`Vue ${index + 1}`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {selectedImage === index && (
                        <div className="absolute inset-0 bg-red-600 bg-opacity-10" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  {selectedImage + 1} / {annonce.apartmentId.images.length} photos
                </span>
                <button 
                  onClick={() => setSelectedImage((prev) => 
                    prev === annonce.apartmentId.images.length - 1 ? 0 : prev + 1
                  )}
                  className="text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                >
                  Photo suivante
                  <MdNavigateNext  size={20}/>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pb-16">
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Header Section */}
          <div className="p-6 sm:p-8 lg:p-10 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{annonce.title}</h1>
                <p className="text-gray-500 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {annonce?.apartmentId?.buildingId?.address?.street}, {annonce?.apartmentId?.buildingId?.address?.city}
                </p>
              </div>
              <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg px-6 py-4 text-white">
                <p className="text-sm uppercase tracking-wide">Prix</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {annonce?.apartmentId?.price ? new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: "USD",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(annonce.apartmentId.price.amount) : 'Prix non disponible'}
                  </p>
                  <span className="text-sm opacity-75">
                    {annonce?.apartmentId?.price?.paymentFrequency === 'mensuel' ? '/mois' : ''}
                  </span>
                </div>
                {annonce?.price?.negotiable && (
                  <p className="text-sm mt-1 opacity-75">Prix négociable</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6 sm:p-8 lg:p-10">
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-600 leading-relaxed">{annonce.description}</p>
              </div>

              {/* Property Details */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Détails du bien</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto mb-2 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m-2 0h5m-9 0h9m5-5v-5a2 2 0 012-2h5a2 2 0 012 2v5m-4 0h2m4-4h2" />
                    </svg>
                    <p className="text-sm text-gray-500">Type</p>
                    <p className="font-semibold text-gray-900 capitalize">{annonce?.apartmentId?.type || 'Non spécifié'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto mb-2 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5m-5 5v12m0 0l4-4m4 4l4-4" />
                    </svg>
                    <p className="text-sm text-gray-500">Surface</p>
                    <p className="font-semibold text-gray-900">{annonce?.apartmentId?.surface ? `${annonce.apartmentId.surface} m²` : 'Non spécifié'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto mb-2 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <p className="text-sm text-gray-500">Chambres</p>
                    <p className="font-semibold text-gray-900">{annonce?.apartmentId?.rooms || 'Non spécifié'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto mb-2 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h18v18H3zM3 12h18M12 3v18" />
                    </svg>
                    <p className="text-sm text-gray-500">Salles de bain</p>
                    <p className="font-semibold text-gray-900">{annonce?.apartmentId?.bathrooms || 'Non spécifié'}</p>
                  </div>
                </div>
              </div>

              {/* Features */}
              {annonce?.apartmentId?.buildingId?.features && annonce.apartmentId.buildingId.features.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Caractéristiques</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {annonce.apartmentId.buildingId.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700 capitalize">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Availability */}
              {annonce?.availability && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Disponibilité</h2>
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-700">
                      {annonce.availability.immediateMove ? 'Disponible immédiatement' : 'Disponibilité à confirmer'}
                    </span>
                  </div>
                </div>
              )}

              {/* Requirements */}
              {annonce?.requirements?.documents && annonce.requirements.documents.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Documents requis</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {annonce.requirements.documents.map((document, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.5 9.5c.83-.83 2.17-.83 3 0 .83.83.83 2.17 0 3-.83.83-2.17.83-3 0-.83-.83-.83-2.17 0-3z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12c0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3-3 1.34-3 3zm1.5 0c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5-1.5.67-1.5 1.5z" />
                        </svg>
                        <span className="text-gray-700">{document}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Card */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Contact</h2>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{annonce?.publisher?.firstName} {annonce?.publisher?.lastName}</p>
                  <p className="text-sm text-gray-500">Propriétaire</p>
                </div>
              </div>
              <div className="space-y-3">
                {annonce?.contactPreferences?.phone && annonce?.publisher?.phone && (
                  <a
                    href={`tel:${annonce.publisher.phone}`}
                    className="w-full bg-red-600 text-white py-3 px-4 rounded-lg flex items-center justify-center gap-2 hover:bg-red-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Appeler
                  </a>
                )}
                {annonce?.contactPreferences?.whatsapp && annonce?.publisher?.phone && (
                  <a
                    href={`https://wa.me/${annonce.publisher.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Annonce;